import { type RefObject, useEffect, useState } from "react";
import { useAemNavOffset } from "../../../common-deprecated/hooks";
import type { UscLabelType } from "../utils/labels";
import type { ScrollTargetSelector } from "../utils/constants";
import useMenuSectionSelectors from "./useMenuSectionSelectors";
import { getActiveScrollTarget } from "../utils/helpers";
import { getScrollBehavior } from "../../../common-deprecated/utils";
import { useWindowDimensionsContext } from "../../../common-deprecated/containers/WindowDimensionsProvider";

type UseHorizontalMenuProps = {
    menuRef: RefObject<HTMLDivElement>;
    sliderRef: RefObject<HTMLDivElement | HTMLUListElement>;
    BREADCRUMBS_HEIGHT: number;
    HORIZONTAL_MENU_HEIGHT: number;
    EXTRA_DETECTION_MARGIN: number;
    ARROW_WIDTH: number;
    EXTRA_SCROLL_MARGIN: number;
};

type UseHorizontalMenuReturnType = {
    selectors: ScrollTargetSelector[];
    activeSelector: ScrollTargetSelector;
    scrollSelectorsLabelMap: Record<ScrollTargetSelector, UscLabelType>;
    scrollTo: (selector: ScrollTargetSelector) => void;
    // Items below are specifically used for the left/right arrow navigation
    handleScroll: (dir: "left" | "right") => void;
    showLeftArrow: boolean;
    showRightArrow: boolean;
};

const useHorizontalMenu = ({
    menuRef,
    sliderRef,
    BREADCRUMBS_HEIGHT,
    HORIZONTAL_MENU_HEIGHT,
    EXTRA_DETECTION_MARGIN,
    ARROW_WIDTH,
    EXTRA_SCROLL_MARGIN,
}: UseHorizontalMenuProps): UseHorizontalMenuReturnType => {
    // ----------------------------------------------------------------------
    // Slider
    // ----------------------------------------------------------------------
    const windowWidth = useWindowDimensionsContext().width;
    const [menuWidth, setMenuWidth] = useState<number>(0);
    const [menuChildrenWidth, setMenuChildrenWidth] = useState<number>(0);
    const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
    const [showRightArrow, setShowRightArrow] = useState<boolean>(false);
    const [sliderScrollLeft, setSliderScrollLeft] = useState<number>(0);

    const { selectors, scrollSelectorsLabelMap, scrollTo } = useMenuSectionSelectors(
        HORIZONTAL_MENU_HEIGHT,
        BREADCRUMBS_HEIGHT,
        EXTRA_DETECTION_MARGIN,
        ARROW_WIDTH,
        EXTRA_SCROLL_MARGIN,
    );

    const navOffset = useAemNavOffset();
    // The selector that is currently in view
    const [activeSelector, setActiveSelector] = useState(selectors[0]);

    // ----------------------------------------------------------------------
    // Slider Navigation
    // ----------------------------------------------------------------------
    const handleScroll = (dir: "left" | "right"): void => {
        const behavior = getScrollBehavior();
        const scrollAmount = menuChildrenWidth / 2;

        sliderRef.current?.scrollBy({
            left: dir === "left" ? -scrollAmount : scrollAmount,
            behavior,
        });
    };

    // Calculate widths on resize
    useEffect(() => {
        if (menuRef.current && sliderRef.current) {
            const { clientWidth } = menuRef.current;
            const { scrollWidth } = sliderRef.current;

            setMenuWidth(clientWidth);
            setMenuChildrenWidth(scrollWidth);
        }
    }, [selectors.length, menuWidth, windowWidth, menuRef, sliderRef]);

    // Check if we need to show the arrows
    useEffect(() => {
        const showLeft = sliderScrollLeft > 0;
        const showRight = sliderScrollLeft + menuWidth < menuChildrenWidth;

        setShowLeftArrow(showLeft);
        setShowRightArrow(showRight);
    }, [sliderScrollLeft, menuWidth, menuChildrenWidth]);

    // Listen to scroll events to update the scrollLeft position
    useEffect(() => {
        const currentSlider = sliderRef.current;

        const updateScrollLeft = (): void => {
            setSliderScrollLeft(currentSlider?.scrollLeft || 0);
        };

        currentSlider?.addEventListener("scroll", updateScrollLeft);
        return () => {
            currentSlider?.removeEventListener("scroll", updateScrollLeft);
        };
    }, [sliderRef]);

    // When the user scrolls on the page causing the active selector to change,
    // scroll the active selector into view
    useEffect(() => {
        const selectorIndex = selectors.indexOf(activeSelector);
        if (selectorIndex === -1) return;

        const selector = sliderRef.current?.children[selectorIndex];
        if (!selector) return;

        const { left, right } = selector.getBoundingClientRect();
        const { left: containerLeft, right: containerRight } = sliderRef.current?.getBoundingClientRect() || {
            left: 0,
            right: 0,
        };

        const behavior = getScrollBehavior();

        const padding = 50;
        if (left < containerLeft + padding) {
            try {
                sliderRef.current?.scrollBy({
                    left: left - containerLeft - padding,
                    behavior,
                });
            } catch (ignored) {
                sliderRef.current.scrollLeft += left - containerLeft - padding;
            }
        } else if (right > containerRight - padding) {
            try {
                sliderRef.current?.scrollBy({
                    left: right - containerRight + padding,
                    behavior,
                });
            } catch (ignored) {
                sliderRef.current.scrollLeft += right - containerRight + padding;
            }
        }
    }, [activeSelector, selectors, sliderRef]);

    // ----------------------------------------------------------------------
    // Active item
    // ----------------------------------------------------------------------
    useEffect(() => {
        const updateScroll = (): void => {
            const offset = navOffset + BREADCRUMBS_HEIGHT + HORIZONTAL_MENU_HEIGHT + EXTRA_DETECTION_MARGIN;
            const selector = getActiveScrollTarget(selectors, offset);
            if (selector) setActiveSelector(selector);
        };

        window.addEventListener("scroll", updateScroll);
        return () => {
            window.removeEventListener("scroll", updateScroll);
        };
    }, [
        menuWidth,
        selectors.length,
        navOffset,
        selectors,
        BREADCRUMBS_HEIGHT,
        HORIZONTAL_MENU_HEIGHT,
        EXTRA_DETECTION_MARGIN,
    ]);

    return {
        scrollSelectorsLabelMap,
        selectors,
        activeSelector,
        scrollTo,
        handleScroll,
        showLeftArrow,
        showRightArrow,
    };
};

export default useHorizontalMenu;
